/**
 * Attribution marketing : d'où vient le visiteur qui finit par s'inscrire ?
 *
 * Capturé une fois au chargement de l'app (index.jsx), puis envoyé avec
 * l'inscription (/api/register) où il est enregistré sur l'utilisateur.
 * Le serveur normalise et déduit la source quand il n'y a pas d'UTM
 * (AcquisitionAttributionService).
 *
 * Modèle « dernier contact non direct » (celui de GA4) : une visite tagguée
 * (UTM, ref, gclid, fbclid) ou venant d'un site externe remplace la précédente ;
 * une visite directe ne l'écrase jamais. Validité : 90 jours.
 *
 * RGPD : le localStorage est un traceur au sens de la CNIL. Sans accord cookies,
 * rien n'est écrit sur l'appareil : la provenance de la visite en cours reste en
 * mémoire et n'est envoyée que si l'inscription a lieu pendant cette visite.
 * La persistance entre visites n'est activée qu'après acceptation
 * (persistAttribution, appelé par le bandeau) et effacée en cas de refus.
 */

const STORAGE_KEY = 'fd_attribution';
const FIRST_VISIT_KEY = 'fd_first_visit_at';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

// Domaines de retour de paiement : un retour Stripe/Klarna/PayPal n'est pas une source.
const IGNORED_REFERRER_HOSTS = [
  'flowdino.com',
  'stripe.com',
  'klarna.com',
  'paypal.com',
  'localhost'
];

// Liens courts pour les supports où la place manque (SMS limité à 160 caractères) :
// la page d'arrivée vaut UTM. La route React correspondante redirige vers l'accueil.
export const SHORT_LINKS = {
  '/sms': { utm_source: 'brevo', utm_medium: 'sms', utm_campaign: 'prospection-sms' }
};

// Provenance de la visite en cours, en mémoire uniquement (null = visite directe).
let sessionTouch = null;
const sessionStartedAt = new Date().toISOString();

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Stockage indisponible (navigation privée stricte) : pas d'attribution, pas d'erreur.
  }
};

const safeRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // idem
  }
};

const hasConsent = () => safeGet('cookieConsent') === 'accepted';

const externalReferrer = () => {
  if (!document.referrer) return null;
  try {
    const host = new URL(document.referrer).hostname;
    if (IGNORED_REFERRER_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))) {
      return null;
    }
    // Origine seule : le chemin du référent peut contenir des données personnelles.
    return new URL(document.referrer).origin;
  } catch (e) {
    return null;
  }
};

export const captureAttribution = () => {
  const params = new URLSearchParams(window.location.search);
  const touch = { ...(SHORT_LINKS[window.location.pathname] || {}) };
  UTM_PARAMS.forEach((name) => {
    const value = params.get(name);
    if (value) touch[name] = value.slice(0, 150);
  });
  ['ref', 'gclid', 'fbclid'].forEach((name) => {
    const value = params.get(name);
    if (value) touch[name] = name === 'ref' ? value.slice(0, 50) : '1';
  });
  const referrer = externalReferrer();
  if (referrer) touch.referrer = referrer;

  if (Object.keys(touch).length > 0) {
    touch.landing_page = window.location.pathname.slice(0, 255);
    touch.captured_at = sessionStartedAt;
    sessionTouch = touch;
  }

  if (hasConsent()) {
    persistAttribution();
  }
};

/**
 * Écrit la provenance sur l'appareil. À n'appeler qu'avec l'accord cookies.
 */
export const persistAttribution = () => {
  if (!safeGet(FIRST_VISIT_KEY)) {
    safeSet(FIRST_VISIT_KEY, sessionStartedAt);
  }
  // Visite directe : on garde le contact précédent.
  if (sessionTouch) {
    safeSet(STORAGE_KEY, JSON.stringify(sessionTouch));
  }
};

/**
 * Retrait du consentement : on efface ce qui avait été stocké.
 */
export const clearAttribution = () => {
  safeRemove(STORAGE_KEY);
  safeRemove(FIRST_VISIT_KEY);
};

/**
 * Données à joindre à l'inscription (null si la provenance est inconnue).
 */
export const getAttribution = () => {
  if (!hasConsent()) {
    // Sans accord : uniquement la visite en cours. Une visite directe sans
    // historique est « inconnue », pas « directe » : le visiteur a pu venir
    // d'une campagne lors d'une visite précédente.
    return sessionTouch ? { ...sessionTouch } : null;
  }

  let touch = null;
  try {
    touch = JSON.parse(safeGet(STORAGE_KEY) || 'null');
  } catch (e) {
    touch = null;
  }

  if (touch && Date.now() - new Date(touch.captured_at).getTime() > MAX_AGE_MS) {
    touch = null;
  }

  const firstVisitAt = safeGet(FIRST_VISIT_KEY);
  if (!touch && !firstVisitAt) return null;

  return { ...(touch || {}), first_visit_at: firstVisitAt };
};

/**
 * Code d'affiliation vu pendant la navigation (ex. lien /?ref=CODE sur l'accueil,
 * puis clic sur « Essai gratuit » : le paramètre est perdu dans l'URL de /register).
 */
export const getAttributedAffiliateCode = () => getAttribution()?.ref || null;
